import React, { useState, useEffect } from 'react';
import {
  CRow,
  CCol,
  CCard,
  CCardBody,
  CCardHeader,
  CSpinner,
  CTable,
  CBadge,
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilUser, cilChart, cilPeople, cilDollar, cilArrowTop, cilArrowBottom } from '@coreui/icons';
import { resellerAPI } from '../services/api';

const Dashboard = () => {
  const [reseller, setReseller] = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [profileRes, clientsRes] = await Promise.all([
        resellerAPI.getProfile(),
        resellerAPI.getMyClients()
      ]);

      if (profileRes.data.success) {
        setReseller(profileRes.data.reseller);
      }

      if (clientsRes.data.success) {
        setClients(clientsRes.data.clients);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-50">
        <CSpinner color="primary" />
      </div>
    );
  }

  return (
    <div>
      <div className="page-header mb-4">
        <h1 className="page-title">Dashboard</h1>
        <p className="page-subtitle">Welcome back, {reseller?.name}</p>
      </div>
      
      {reseller && (
        <CRow className="mb-4">
          <CCol md={6} lg={3}>
            <CCard className="stats-card border-start border-primary">
              <CCardBody>
                <CIcon icon={cilUser} className="stats-icon text-primary" />
                <div className="stats-value">{reseller.resellerId}</div>
                <div className="stats-label">Reseller ID</div>
              </CCardBody>
            </CCard>
          </CCol>
          
          <CCol md={6} lg={3}>
            <CCard className="stats-card border-start border-success">
              <CCardBody>
                <CIcon icon={cilChart} className="stats-icon text-success" />
                <div className="stats-value">
                  <span className="coming-soon-highlight">Coming Soon</span>
                </div>
                <div className="stats-label">Active Subscriptions</div>
              </CCardBody>
            </CCard>
          </CCol>
          
          <CCol md={6} lg={3}>
            <CCard className="stats-card border-start border-info">
              <CCardBody>
                <CIcon icon={cilPeople} className="stats-icon text-info" />
                <div className="stats-value">{clients.length}</div>
                <div className="stats-label">Total Clients</div>
              </CCardBody>
            </CCard>
          </CCol>
          
          <CCol md={6} lg={3}>
            <CCard className="stats-card border-start border-warning">
              <CCardBody>
                <CIcon icon={cilDollar} className="stats-icon text-warning" />
                <div className="stats-value">{reseller.margin}%</div>
                <div className="stats-label">Profit Margin</div>                
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      )}

      <CRow>
        <CCol lg={8}>
          <CCard>
            <CCardHeader>
              <h5 className="card-title mb-0">Recent Clients</h5>
            </CCardHeader>
            <CCardBody>
              {clients.length > 0 ? (
                <div className="table-responsive">
                  <CTable hover>
                    <thead>
                      <tr>
                        <th>Client Name</th>
                        <th>Contact</th>
                        <th>Email</th>
                        <th>Status</th>
                        <th>Location</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clients.slice(0, 5).map((client) => (
                        <tr key={client.id}>
                          <td>
                            <strong>{client.name}</strong>
                          </td>
                          <td>{client.client_full_name}</td>
                          <td>
                            <a href={`mailto:${client.client_owner_email}`}>
                              {client.client_owner_email}
                            </a>
                          </td>
                          <td>
                            <CBadge color="success" shape="rounded-pill">
                              Active
                            </CBadge>
                          </td>
                          <td>
                            {client.billing_city}, {client.billing_state}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </CTable>
                </div>
              ) : (
                <div className="text-center py-4">
                  <CIcon icon={cilPeople} size="3xl" className="text-muted mb-3" />
                  <h5 className="text-muted">No clients found</h5>
                  <p className="text-muted">You don't have any clients assigned yet.</p>
                </div>
              )}
            </CCardBody>
          </CCard>
        </CCol>
        
        <CCol lg={4}>
          <CCard>
            <CCardHeader>
              <h5 className="card-title mb-0">Quick Stats</h5>
            </CCardHeader>
            <CCardBody>
              <div className="quick-stats">
                <div className="stat-item d-flex justify-content-between align-items-center py-2">
                  <span>Active Clients</span>
                  <span className="coming-soon-highlight">Coming Soon</span>
                </div>
                <div className="stat-item d-flex justify-content-between align-items-center py-2">
                  <span>Pending Actions</span>
                  <span className="coming-soon-highlight">Coming Soon</span>
                </div>
                <div className="stat-item d-flex justify-content-between align-items-center py-2">
                  <span>Revenue This Month</span>
                  <span className="coming-soon-highlight">Coming Soon</span>
                </div>
                <div className="stat-item d-flex justify-content-between align-items-center py-2">
                  <span>Support Tickets</span>
                  <span className="coming-soon-highlight">Coming Soon</span>
                </div>
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      <style jsx>{`
  .coming-soon-highlight {
    background: linear-gradient(135deg, #74b9ff 0%, #0984e3 100%);
    color: white;
    font-weight: 600;
    padding: 5px 14px;
    border-radius: 6px;
    font-size: 0.8rem;
    animation: softGlow 4s ease-in-out infinite;
    box-shadow: 0 2px 10px rgba(116, 185, 255, 0.4);
    position: relative;
    overflow: hidden;
  }
  
  .coming-soon-highlight::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
    animation: shimmer 3s infinite;
  }
  
  @keyframes softGlow {
    0%, 100% { 
      transform: scale(1);
      box-shadow: 0 2px 10px rgba(116, 185, 255, 0.4);
    }
    50% { 
      transform: scale(1.02);
      box-shadow: 0 4px 20px rgba(116, 185, 255, 0.6);
    }
  }
  
  @keyframes shimmer {
    0% { left: -100%; }
    100% { left: 100%; }
  }
`}</style>
    </div>
  );
};

export default Dashboard;